import { inject } from '@angular/core';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import {
  entityConfig,
  removeAllEntities,
  removeEntities,
  setAllEntities,
  upsertEntities,
  upsertEntity,
  withEntities,
} from '@ngrx/signals/entities';
import { firstValueFrom } from 'rxjs';
import { ContentService, GroupService } from '@thiememeulenhoff/ng-api-gateway';
import {
  ArrangeLevel,
  Arrangement,
  CreateArrangementInterface,
  Group,
  Method,
  MethodDetail,
  Resource,
  ResourceInput,
  ResourceWithMethodSlug,
  UpdateArrangementInterface,
  VdexNode,
} from '@thiememeulenhoff/types';

const arrangementsConfig = entityConfig({
  entity: {} as Arrangement,
  collection: 'arrangements',
  selectId: (e: Arrangement) => e.code,
});

const arrangementTemplatesConfig = entityConfig({
  entity: {} as Arrangement,
  collection: 'arrangementTemplates',
  selectId: (e: Arrangement) => e.code,
});

const methodsConfig = entityConfig({
  entity: {} as Method,
  collection: 'methods',
  selectId: (e: Method) => e.slug,
});

const resourcesConfig = entityConfig({
  entity: {} as ResourceWithMethodSlug,
  collection: 'resources',
  selectId: (e: ResourceWithMethodSlug) => e.contentIdentifier,
});

const resourcesInArrangementConfig = entityConfig({
  entity: {} as Resource,
  collection: 'resourcesInArrangement',
  selectId: (e: Resource) => e.contentIdentifier,
});

const groupsConfig = entityConfig({
  entity: {} as Group,
  collection: 'groups',
  selectId: (e: Group) => e.id,
});

type ArrangeState = {
  learningGoals: Array<VdexNode>;
};

const initialState: ArrangeState = {
  learningGoals: [],
};

export const ArrangeStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withEntities(arrangementsConfig),
  withEntities(arrangementTemplatesConfig),
  withEntities(methodsConfig),
  withEntities(resourcesConfig),
  withEntities(resourcesInArrangementConfig),
  withEntities(groupsConfig),
  withMethods(
    (
      store,
      contentService = inject(ContentService),
      groupService = inject(GroupService),
    ) => {
      function replaceArrangements(arrangements: Array<Arrangement>): void {
        patchState(store, setAllEntities(arrangements, arrangementsConfig));

        const methods = [
          ...new Map(
            arrangements
              .map((arrangement) => arrangement.methodObject)
              .map((item) => [item.slug, item]),
          ).values(),
        ];

        patchState(store, setAllEntities(methods, methodsConfig));
      }

      function upsertResources(
        resources: Array<ResourceWithMethodSlug>,
        methodSlug = '',
      ): void {
        if (methodSlug) {
          patchState(
            store,
            removeEntities(
              (resource) => resource.methodSlug === methodSlug,
              resourcesConfig,
            ),
          );
        }
        patchState(store, upsertEntities(resources, resourcesConfig));
      }

      return {
        async fetchMethod(methodSlug: string): Promise<MethodDetail> {
          return firstValueFrom(contentService.fetchMethodBySlug(methodSlug));
        },

        async fetchMethods(): Promise<void> {
          const methods = await firstValueFrom(contentService.fetchMethods());
          patchState(store, upsertEntities(methods, methodsConfig));
        },

        async fetchArrangement(code: string): Promise<Arrangement> {
          const arrangement = await firstValueFrom(
            contentService.fetchArrangementByCode(code),
          );
          patchState(store, upsertEntity(arrangement, arrangementsConfig));

          return store.arrangementsEntityMap()[arrangement.code] as Arrangement;
        },

        async fetchArrangementsByMethodSlug(methodSlug: string): Promise<void> {
          const arrangements = await firstValueFrom(
            contentService.fetchUserDefinedArrangements(methodSlug),
          );
          replaceArrangements(arrangements);
        },

        async fetchPredefinedArrangementsByMethodSlug(
          methodSlug: string,
        ): Promise<void> {
          const arrangements = await firstValueFrom(
            contentService.fetchPredefinedArrangements(methodSlug),
          );
          replaceArrangements(arrangements);
        },

        async fetchAvailableLearningItems(
          methodSlug: string,
          arrangeLevel: ArrangeLevel,
        ): Promise<Array<ResourceWithMethodSlug>> {
          let mapped: Array<ResourceWithMethodSlug> = [];
          try {
            const resources = await firstValueFrom(
              contentService.fetchAvailableLearningItems(
                methodSlug,
                arrangeLevel,
              ),
            );

            mapped = resources.map((resource) => {
              const newResource = {
                ...resource,
                originalChildren: resource.children,
                methodSlug,
              } as ResourceWithMethodSlug;

              delete newResource.children;

              return newResource;
            });
          } catch {
            // mapped stays []
          }

          upsertResources(mapped, methodSlug);

          return mapped;
        },

        async fetchAvailableLearningItemChildren(
          arrangementCode: string,
          contentIdentifier: string,
          isModuleStream: boolean,
        ): Promise<Array<Resource>> {
          try {
            const resources = await firstValueFrom(
              contentService.fetchResourceByArrangementCode<Resource>(
                arrangementCode,
                contentIdentifier,
                1,
                undefined,
                { include: ['module', 'chapter', 'paragraph'] },
              ),
            );

            const firstResource = resources[0];

            if (!firstResource.children) {
              return [];
            }

            return firstResource.children.filter(
              (child) =>
                child.digitalResourceType ===
                (isModuleStream ? 'chapter' : 'paragraph'),
            );
          } catch {
            return [];
          }
        },

        async fetchVdexDefinition(
          arrangementCode: string,
          apiField: string,
        ): Promise<Array<VdexNode>> {
          const learningGoals = await firstValueFrom(
            contentService.fetchVdexDefinition(arrangementCode, apiField),
          );
          patchState(store, { learningGoals });

          return learningGoals;
        },

        async createArrangement(
          data: Omit<CreateArrangementInterface, 'code' | 'userDelete'>,
        ) {
          return firstValueFrom(
            contentService.createArrangement({ ...data, userDelete: false }),
          );
        },

        async updateArrangement(
          data: Omit<UpdateArrangementInterface, 'userDelete'>,
          arrangementId: string,
        ): Promise<Arrangement> {
          const arrangement = await firstValueFrom(
            contentService.updateArrangement(
              { ...data, userDelete: false },
              arrangementId,
            ),
          );
          patchState(store, upsertEntity(arrangement, arrangementsConfig));

          return arrangement;
        },

        async fetchTemplateArrangements(
          methodSlug: string,
        ): Promise<Array<Arrangement>> {
          const templates = await firstValueFrom(
            contentService.fetchTemplateArrangements(methodSlug),
          );
          patchState(
            store,
            setAllEntities(templates, arrangementTemplatesConfig),
          );

          return templates;
        },

        async deleteArrangement(
          arrangement: Arrangement,
          lastModifiedBy: string,
        ): Promise<void> {
          const data = {
            audienceGroups: arrangement.audienceGroups,
            description: arrangement.description,
            method: arrangement.method,
            title: arrangement.title,
            type: arrangement.type,
            userDelete: true,
            lastModifiedBy,
          };

          await firstValueFrom(
            contentService.updateArrangement(data, arrangement.code),
          );
          patchState(
            store,
            removeEntities([arrangement.code], arrangementsConfig),
          );
        },

        removeUnlinkedArrangement(arrangement: Arrangement): void {
          patchState(
            store,
            removeEntities([arrangement.code], arrangementsConfig),
          );
        },

        async fetchGroups(methodSlug: string): Promise<Array<Group>> {
          const groupIndex = await firstValueFrom(
            groupService.fetchGroups(methodSlug, false, true),
          );
          patchState(store, upsertEntities(groupIndex.items, groupsConfig));

          return groupIndex.items;
        },

        async fetchArrangementContent(
          arrangementCode: string,
          withChildren = false,
        ): Promise<Array<Resource>> {
          patchState(store, removeAllEntities(resourcesInArrangementConfig));

          try {
            const arrangements = await firstValueFrom(
              contentService.fetchResourceByArrangementCode<Resource>(
                arrangementCode,
                '',
                withChildren ? 2 : 1,
                undefined,
                { include: ['module', 'chapter', 'paragraph'] },
              ),
            );

            const content = arrangements[0]?.children ?? [];
            patchState(
              store,
              setAllEntities(content, resourcesInArrangementConfig),
            );

            return store.resourcesInArrangementEntities();
          } catch {
            return [];
          }
        },

        removeArrangementContent(resource: Resource): void {
          patchState(
            store,
            removeEntities(
              [resource.contentIdentifier],
              resourcesInArrangementConfig,
            ),
          );
        },

        addArrangementContent(resource: Resource): void {
          patchState(
            store,
            upsertEntity(resource, resourcesInArrangementConfig),
          );
        },

        updateArrangementContent(resources: Array<Resource>): void {
          patchState(
            store,
            setAllEntities(resources, resourcesInArrangementConfig),
          );
        },

        async saveArrangementContent(
          arrangementCode: string,
          content: Array<ResourceInput>,
        ): Promise<Array<Resource>> {
          return firstValueFrom(
            contentService.saveArrangementContent(arrangementCode, content),
          );
        },

        updateLearningItemChildren(
          learningItemToBeUpdated: Resource,
          paragraphs: Array<Resource>,
        ): void {
          const arrangementContent = store.resourcesInArrangementEntities();
          const activeLearningItem = arrangementContent.find(
            (learningItem) =>
              learningItem.contentIdentifier ===
              learningItemToBeUpdated.contentIdentifier,
          );

          if (activeLearningItem) {
            activeLearningItem.children = paragraphs;
          }

          patchState(
            store,
            setAllEntities(arrangementContent, resourcesInArrangementConfig),
          );
        },
      };
    },
  ),
);
